import { DossierPickupOrders } from '../components/PickupOrders';
import { can, clientName, dossierLabel, formatDate } from '@clim/client-core';
import {
  DOSSIER_EDITABLE,
  DOSSIER_EDITABLE_BY_CONTROL,
  availableTransitions,
  dossierMachine,
  type CommentRecord,
  type DossierStatus,
} from '@clim/domain';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DossierFieldsEditor, type DossierDraft } from '../components/DossierFields';
import { ContractPanel } from '../components/ContractPanel';
import { PortalLinks } from '../components/PortalLinks';
import { DossierFiles } from '../components/DossierFiles';
import { DossierInterventions } from '../components/Interventions';
import { DossierPayments } from '../components/DossierPayments';
import { DossierSav } from '../components/DossierSav';
import { DossierLines } from '../components/DossierLines';
import { TransitionDialog } from '../components/TransitionDialog';
import { TypeTag } from '../components/TypeTag';
import { Empty, ErrorBox, Field, Modal, Select, StatusBadge, SyncTag, Textarea } from '../components/ui';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';

interface History {
  statuses: { id: string; from: string | null; to: DossierStatus; reason: string | null; comment: string | null; at: string; by: string | null; byRoles: string[] }[];
  assignments: { id: string; userName: string; role: string; assignedAt: string; unassignedAt: string | null }[];
}

const ACTION_LABEL: Partial<Record<DossierStatus, string>> = {
  submitted: 'Soumettre au contrôle',
  to_correct: 'Renvoyer « À corriger »',
  validated: 'Valider',
  to_schedule: 'Passer « À planifier »',
  cancelled: 'Annuler le dossier',
};
const ACCESS_LABEL = { none: 'Aucun', ladder: 'Échelle', nacelle: 'Nacelle' } as const;

export function DossierDetailPage() {
  const { id = '' } = useParams();
  const engine = useEngine();
  const { me, api } = useReady();
  const online = useOnline();
  const navigate = useNavigate();
  const d = engine.get('dossier', id);
  const [target, setTarget] = useState<DossierStatus | null>(null);
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [history, setHistory] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!online || !d?.reference) return;
    api.request<History>('GET', `/v1/dossiers/${id}/history`).then(setHistory, () => setHistory(null));
  }, [api, id, online, d?.version, d?.reference]);

  if (!d) return <Empty>Dossier introuvable (ou plus accessible).</Empty>;
  const client = engine.get('client', d.clientId);
  const type = engine.get('dossier_type', d.dossierTypeId);
  const reason = d.statusReasonId ? engine.get('reason', d.statusReasonId) : undefined;
  const wall = d.installWallTypeId ? engine.get('reason', d.installWallTypeId) : undefined;
  // chantier partenaire (sous-traitance de pose) : brouillon → « À planifier » directement, jamais le parcours de vente
  const partner = d.partnerId ? engine.get('reason', d.partnerId) : undefined;
  const transitions = availableTransitions(dossierMachine, d.status, 'user')
    .filter((t) => t.permission && can(me, t.permission))
    .filter((t) =>
      d.partnerId
        ? t.to !== 'submitted' && t.to !== 'validated'
        : !(d.status === 'draft' && t.to === 'to_schedule'),
    );
  const editable =
    (DOSSIER_EDITABLE.includes(d.status) && can(me, 'dossier.update')) || (DOSSIER_EDITABLE_BY_CONTROL.includes(d.status) && can(me, 'dossier.control'));
  const terminal = dossierMachine.terminal.includes(d.status);
  const comments = engine
    .list('comment')
    .filter((c) => c.dossierId === d.id)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const controlReason = d.lastControl?.reasonId ? engine.get('reason', d.lastControl.reasonId) : undefined;

  const go = (to: DossierStatus) => {
    const def = transitions.find((t) => t.to === to);
    if (def?.reasonCategory || def?.requiresComment) return setTarget(to);
    setError(null);
    engine.transitionDossier(d.id, to).catch((e) => {
      const fe = fieldErrors(e);
      setError(Object.keys(fe).length ? `Dossier incomplet : ${Object.values(fe).join(' · ')}` : errorMessage(e));
    });
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>{clientName(client)}</h1>
          <p className="muted">
            {d.reference ?? 'Référence attribuée à la synchronisation'} · {dossierLabel(d, type?.label)}
          </p>
          <TypeTag type={type} /> <SyncTag state={engine.syncState('dossier', d.id)} />
        </div>
        <StatusBadge status={d.status} />
      </div>

      {d.lastControl && (
        <div className={`control-banner control-${d.lastControl.outcome}`} role="status">
          <strong>{d.lastControl.outcome === 'returned' ? '↩ Renvoyé à corriger' : '✔ Validé'}</strong> par {d.lastControl.byName} le{' '}
          {formatDate(d.lastControl.at)}
          {controlReason && <div>Motif : {controlReason.label}</div>}
          {d.lastControl.comment && <div className="pre">« {d.lastControl.comment} »</div>}
        </div>
      )}
      {d.partnerId && (
        <p className="notice partner-notice" role="status">
          Chantier pour le partenaire <strong>{partner?.label ?? '—'}</strong> : pose en sous-traitance, sans vente (ni prix,
          ni signature, ni paiement).
          {d.status === 'draft' && ' Indiquez si c’est en hauteur, ajoutez les photos et la photo du bon du partenaire (catégorie « Bon du partenaire »), choisissez la puissance, puis « Envoyer en planification ».'}
        </p>
      )}
      {reason && d.status === 'cancelled' && <p className="muted">Motif d’annulation : {reason.label}</p>}
      {can(me, 'dossier.control') && d.status === 'submitted' && <DuplicateAlert clientId={d.clientId} />}

      {transitions.length > 0 && (
        <div className="actions wrap">
          {transitions.map((t) => (
            <button
              key={t.to}
              className={`btn ${t.to === 'cancelled' ? 'danger-outline' : t.to === 'to_correct' ? 'warning-outline' : 'primary'}`}
              onClick={() => go(t.to)}
            >
              {d.partnerId && t.to === 'to_schedule' ? 'Envoyer en planification' : (ACTION_LABEL[t.to] ?? dossierMachine.labels[t.to])}
            </button>
          ))}
        </div>
      )}
      <ErrorBox message={error} />

      <div className="card">
        <div className="card-head">
          <h2>Dossier</h2>
          {editable && (
            <button className="btn small" onClick={() => setEditing(true)}>
              Modifier
            </button>
          )}
        </div>
        {d.description && <p className="pre">{d.description}</p>}
        <dl className="kv">
          <dt>Client</dt>
          <dd>
            <Link to={`/clients/${d.clientId}`}>{clientName(client)}</Link> {client?.phone && <a href={`tel:${client.phone}`}>{client.phone}</a>}
          </dd>
          <dt>Adresse d’intervention</dt>
          <dd>
            {[d.siteAddressLine1, d.sitePostalCode, d.siteCity].filter(Boolean).join(', ') ||
              [client?.addressLine1, client?.postalCode, client?.city].filter(Boolean).join(', ') ||
              '—'}
          </dd>
          {d.geoLat != null && (
            <>
              <dt>Position relevée</dt>
              <dd>
                <a href={`https://maps.google.com/?q=${d.geoLat},${d.geoLng}`} target="_blank" rel="noreferrer">
                  Voir sur la carte
                </a>{' '}
                <span className="muted">({formatDate(d.geoCapturedAt)})</span>
              </dd>
            </>
          )}
          {type?.settings?.requiresInstallConditions ? (
            <>
              <dt>Support</dt>
              <dd>{wall?.label ?? '—'}</dd>
            </>
          ) : null}
          <dt>En hauteur</dt>
          <dd>
            {d.installAtHeight == null ? '— (à préciser)' : d.installAtHeight ? `Oui · ${ACCESS_LABEL[d.installAccess ?? 'none']} · ~${d.installHeightM ?? '?'} m` : 'Non'}
          </dd>
          <dt>Créé le</dt>
          <dd>{formatDate(d.createdAt)}</dd>
          {d.submittedAt && (
            <>
              <dt>Soumis le</dt>
              <dd>{formatDate(d.submittedAt)}</dd>
            </>
          )}
        </dl>
      </div>

      <DossierLines
        dossier={d}
        editable={
          !d.signature &&
          (((['draft', 'to_correct'] as DossierStatus[]).includes(d.status) && can(me, 'dossier.update')) ||
            ((['draft', 'submitted', 'to_correct'] as DossierStatus[]).includes(d.status) && can(me, 'dossier.control')))
        }
      />

      <DossierFiles dossier={d} />

      {!d.partnerId && <ContractPanel dossier={d} />}
      {!d.partnerId && <DossierPayments dossier={d} />}
      <DossierInterventions dossierId={d.id} />
      <DossierPickupOrders dossier={d} />
      <DossierSav dossier={d} />

      {!d.partnerId && <PortalLinks dossier={d} />}

      <Comments dossierId={d.id} comments={comments} />

      <div className="card">
        <div className="card-head">
          <h2>Affectations</h2>
          {!terminal && can(me, 'dossier.assign') && (
            <button className="btn small" onClick={() => setAssigning(true)} disabled={!online}>
              Changer
            </button>
          )}
        </div>
        {d.assignments.length === 0 ? (
          <Empty>Personne n’est affecté.</Empty>
        ) : (
          <ul className="plain">
            {d.assignments.map((a) => (
              <li key={`${a.userId}-${a.role}`}>
                {a.userName} — {a.role === 'commercial' ? 'Commercial' : 'Technicien'}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <h2>Historique</h2>
        {!online ? (
          <Empty>Disponible en ligne.</Empty>
        ) : !history ? (
          <Empty>—</Empty>
        ) : (
          <ol className="timeline">
            {history.statuses.map((h) => (
              <li key={h.id}>
                <StatusBadge status={h.to} />{' '}
                <span className="muted">
                  {formatDate(h.at)} · {h.by ?? 'système'}
                  {h.byRoles?.length ? ` (${h.byRoles.join(' + ')})` : ''}
                </span>
                {h.reason && <div>Motif : {h.reason}</div>}
                {h.comment && <div className="pre">{h.comment}</div>}
              </li>
            ))}
            {history.assignments.map((a) => (
              <li key={a.id} className="muted">
                {a.userName} ({a.role}) affecté le {formatDate(a.assignedAt)}
                {a.unassignedAt && `, retiré le ${formatDate(a.unassignedAt)}`}
              </li>
            ))}
          </ol>
        )}
      </div>

      {d.status === 'draft' && can(me, 'dossier.delete') && (
        <button
          className="btn danger-outline"
          onClick={() => {
            if (confirm('Supprimer ce brouillon ?')) void engine.deleteDossier(d.id).then(() => navigate('/dossiers'));
          }}
        >
          Supprimer le brouillon
        </button>
      )}

      {d.status !== 'draft' && can(me, 'dossier.delete_any') && (
        <button
          className="btn danger-outline"
          disabled={!online}
          title={online ? undefined : 'Disponible en ligne'}
          onClick={() => {
            const reason = prompt(
              'Supprimer définitivement ce dossier de l’application ?\nIl disparaît pour tout le monde (les données restent tracées).\n\nMotif (obligatoire) :',
            );
            if (!reason?.trim()) return;
            void api
              .request('POST', `/v1/dossiers/${d.id}/admin-delete`, { reason: reason.trim() })
              .then(() => engine.sync())
              .then(
                () => navigate('/dossiers'),
                (e) => alert(errorMessage(e)),
              );
          }}
        >
          Supprimer le dossier (administrateur)
        </button>
      )}

      {target && <TransitionDialog dossier={d} to={target} onClose={() => setTarget(null)} />}
      {editing && <EditDossier id={d.id} onClose={() => setEditing(false)} />}
      {assigning && <AssignDialog id={d.id} onClose={() => setAssigning(false)} />}
    </section>
  );
}

function Comments({ dossierId, comments }: { dossierId: string; comments: CommentRecord[] }) {
  const engine = useEngine();
  const { me } = useReady();
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [error, setError] = useState<string | null>(null);
  const add = async () => {
    try {
      await engine.addComment(dossierId, visibility, body);
      setBody('');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <div className="card">
      <h2>Commentaires</h2>
      {comments.length === 0 && <Empty>Aucun commentaire.</Empty>}
      <ul className="plain comments">
        {comments.map((c) => (
          <li key={c.id} className={c.visibility === 'private' ? 'comment-private' : ''}>
            <div className="muted">
              {c.visibility === 'private' ? '🔒 Privé administrateur · ' : ''}
              {c.authorName} · {formatDate(c.createdAt)} <SyncTag state={engine.syncState('comment', c.id)} />
            </div>
            <div className="pre">{c.body}</div>
          </li>
        ))}
      </ul>
      <div className="form">
        {can(me, 'comment.private.write') && (
          <div className="segmented">
            <button type="button" className={visibility === 'public' ? 'on' : ''} onClick={() => setVisibility('public')}>
              Public / technique
            </button>
            <button type="button" className={visibility === 'private' ? 'on' : ''} onClick={() => setVisibility('private')}>
              🔒 Privé admin
            </button>
          </div>
        )}
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Visible des intervenants autorisés" aria-label="Nouveau commentaire" />
        <ErrorBox message={error} />
        <button className="btn" disabled={!body.trim()} onClick={() => void add()}>
          Ajouter le commentaire
        </button>
      </div>
    </div>
  );
}

function EditDossier({ id, onClose }: { id: string; onClose(): void }) {
  const engine = useEngine();
  const d = engine.get('dossier', id)!;
  const [draft, setDraft] = useState<DossierDraft>({ ...d });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await engine.updateDossier(id, draft);
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(errorMessage(err));
    }
  };
  return (
    <Modal title="Modifier le dossier" onClose={onClose}>
      <div className="form">
        <DossierFieldsEditor value={draft} onChange={setDraft} errors={errors} />
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" onClick={() => void save()}>
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AssignDialog({ id, onClose }: { id: string; onClose(): void }) {
  const engine = useEngine();
  const { api } = useReady();
  const [users, setUsers] = useState<{ id: string; name: string; roles: string[] }[]>([]);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'commercial' | 'technician'>('commercial');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.request<typeof users>('GET', '/v1/assignable-users').then(setUsers, (e) => setError(errorMessage(e)));
  }, [api]);
  const candidates = users.filter((u) => u.roles.includes(role));
  return (
    <Modal title="Affectation" onClose={onClose}>
      <div className="form">
        <div className="segmented">
          {(['commercial', 'technician'] as const).map((r) => (
            <button key={r} type="button" className={role === r ? 'on' : ''} onClick={() => setRole(r)}>
              {r === 'technician' ? 'Technicien' : 'Commercial'}
            </button>
          ))}
        </div>
        {role === 'technician' && <p className="muted">L’attribution aux techniciens avec acceptation/refus arrive avec le module Planning.</p>}
        <Field label="Personne">
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Choisir…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button
            className="btn primary"
            disabled={!userId}
            onClick={() => void engine.assignDossier(id, userId, role).then(onClose, (e) => setError(errorMessage(e)))}
          >
            Affecter
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Au contrôle : doublons possibles du client (téléphone, e-mail, SIRET), avec le commercial concerné. */
export function DuplicateAlert({ clientId }: { clientId: string }) {
  const { api } = useReady();
  const online = useOnline();
  const [dups, setDups] = useState<{ id: string; name: string; city: string | null; owner: string | null }[]>([]);
  useEffect(() => {
    if (!online) return;
    api.request<typeof dups>('GET', `/v1/clients/${clientId}/duplicates`).then(setDups, () => setDups([]));
  }, [api, clientId, online]);
  if (!dups.length) return null;
  return (
    <div className="warning-box" role="status">
      <strong>Doublon possible :</strong>
      <ul className="plain">
        {dups.map((x) => (
          <li key={x.id}>
            <Link to={`/clients/${x.id}`}>{x.name}</Link> {x.city && `· ${x.city}`} {x.owner && `· client de ${x.owner}`}
          </li>
        ))}
      </ul>
    </div>
  );
}
